import React, {Fragment, useContext, useEffect, useState} from "react";
import Button from "../ui-components/Button";
import Modal from "../ui-components/Modal";
import CountryConfigContext from "../CountryConfigContext";
import useGetServiceConfiguration from "@hooks/useGetServiceConfiguration";
import Spinner from "@ui-components/Spinner";

const RenderBlock = ({ deleteBlock }) => {
  return (
    <div className="flex font-bold">
      <p className="capitalize mr-2">{deleteBlock.type}</p>
      <p>{deleteBlock.name || deleteBlock.id}</p>
    </div>
  );
};

const ServiceConfiguratorDeleteBlock = ({
  setDeleteBlock,
  data,
  deleteBlock,
  deleteAndFetch,
}) => {

  // context
  const {
    config: { services },
  } = useContext(CountryConfigContext);

  // hooks
  const getServiceConfiguration = useGetServiceConfiguration();

  // state
  const [associatedBlocks, setAssociatedBlocks] = useState(null);

  // utilities
  const associatedServices = services
    .filter(({associated_services}) => associated_services[deleteBlock.service_id] !== undefined)
  
  useEffect(() => {
    (async () => {
      if (!associatedServices.length) return;
      const associatedBlocks = await Promise.all(
        associatedServices.map(async ({id, active_version}) =>
          await getServiceConfiguration(id, active_version)
        )
      )
      const data = associatedServices
        .map((service, idx) => ({
          service,
          blocks: associatedBlocks[idx].filter((b) => {
              return b.id_source_configuration === deleteBlock.id &&
                b.version <= service.active_version
            }
          ),
        }))
        .filter(({blocks}) => blocks.length > 0);
      setAssociatedBlocks(data);
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  let fatherOf = [];
  let inDependencies = [];

  switch (deleteBlock.type) {
    case "step":
      fatherOf = data.filter((d) => d.step_id === deleteBlock.id);
      break;
    case "recursive_question":
      fatherOf = data.filter((d) => d.recursive_question_id === deleteBlock.id);
      break;
    case "question":
      fatherOf = data.filter((d) => d.input_id === deleteBlock.id);
      inDependencies = data.filter(
        (d) =>
          d.dependencies?.length &&
          d.dependencies.reduce((acc, dep) => {
            return !acc && dep.indexOf(`"inputId":${deleteBlock.id}`) >= 0
              ? true
              : acc;
          }, false)
      );
      break;

    default:
      break;
  }

  let content;
  if (associatedServices.length && !associatedBlocks)
    content = (
      <div className="relative w-full h-40">
        <Spinner styleType="white" />
      </div>
    )
  else
    content = (
      <div className="flex flex-col">
        {fatherOf.length || inDependencies.length || associatedBlocks?.length ? (
          <Fragment>
            <h3 className="text-2xl mb-4">
              This element cannot be deleted
            </h3>
            {fatherOf.length ? (
              <div className="mb-4">
                <p>This element has this children:</p>
                <div className="flex flex-col">
                  {fatherOf.map((e) => (
                    <RenderBlock key={e.id} deleteBlock={e} />
                  ))}
                </div>
              </div>
            ) : null}
            {inDependencies.length ? (
              <div>
                <p>This element is included in this elements' dependencies:</p>
                <div className="flex flex-col">
                  {inDependencies.map((e) => (
                    <RenderBlock key={e.id} deleteBlock={e} />
                  ))}
                </div>
              </div>
            ) : null}
            {associatedBlocks?.length && (
              <div>
                <p>This element is used to populate the following services' blocks:</p>
                <div className="flex flex-col gap-4 mt-4">
                  {associatedBlocks?.map(({service, blocks}) => (
                    <div key={service.id}>
                      <h2 className="text-lg mb-1">{service.name}</h2>
                      <div className="flex flex-col">
                        {blocks.map((e) => (
                          <RenderBlock key={e.id} deleteBlock={e} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Fragment>
        ) : (
          <Fragment>
            <h3 className="text-2xl mb-4">
              Are you sure you want to delete this element?
            </h3>
            <RenderBlock deleteBlock />
            <div className="flex justify-between">
              <Button onClick={() => setDeleteBlock(null)}>CANCEL</Button>
              <Button
                onClick={() => deleteAndFetch(deleteBlock.id)}
                styleType="danger"
              >
                DELETE
              </Button>
            </div>
          </Fragment>
        )}
      </div>
    );

  return (
    <Modal onExit={() => setDeleteBlock(null)}>
      {content}
    </Modal>
  );
};

export default ServiceConfiguratorDeleteBlock;
